import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { Tier, TierProgramDetail } from "@colab/shared";

export type ReorderTiersInput = {
  programId: string;
  orderedIds: string[];
};

type Context = {
  previousDetail: TierProgramDetail | undefined;
};

// PATCH /api/admin/tiers/programs/:programId/tiers/order
//
// Optimistic — DnD reorder of tier columns inside a program. Mirrors bucket
// 5's reorder-fields shape: one PATCH per drop, the whole ordered id list in
// the body, idempotent. Rebuilds `detail.tiers` from the new ordering and
// reassigns position so column rendering matches the server's eventual
// state.
export function useReorderTiers() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, ReorderTiersInput, Context>({
    mutationFn: ({ programId, orderedIds }) =>
      apiFetch<{ ok: true }>(
        `/admin/tiers/programs/${programId}/tiers/order`,
        {
          method: "PATCH",
          body: JSON.stringify({ orderedIds }),
        },
      ),
    onMutate: async ({ programId, orderedIds }) => {
      const key = queryKeys.tiers.program(programId);
      await queryClient.cancelQueries({ queryKey: key });
      const previousDetail = queryClient.getQueryData<TierProgramDetail>(key);
      queryClient.setQueryData<TierProgramDetail>(key, (old) => {
        if (!old) return old;
        const byId = new Map<string, Tier>(old.tiers.map((t) => [t.id, t]));
        // If any id is unknown, bail and let the onSettled refetch resolve.
        if (orderedIds.some((id) => !byId.has(id))) return old;
        const nextTiers: Tier[] = orderedIds.map((id, idx) => {
          const t = byId.get(id);
          // SAFETY: the .some check above guarantees presence.
          if (!t) throw new Error("unreachable: tier id checked above");
          return { ...t, position: idx };
        });
        return { ...old, tiers: nextTiers };
      });
      return { previousDetail };
    },
    onError: (err, { programId }, context) => {
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(
          queryKeys.tiers.program(programId),
          context.previousDetail,
        );
      }
      toast.error(err.message ?? "Failed to reorder tiers");
    },
    onSettled: (_data, _err, { programId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tiers.program(programId),
      });
    },
  });
}
